import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  ASSISTANT_TONES,
  type AssistantTone,
  type CampaignListResponse,
} from '@sdr-agend/admin-schemas'

interface AssistantContextStepProps {
  campaignId: string
  objective: string
  tone: AssistantTone
  onChange: (next: { campaignId: string; objective: string; tone: AssistantTone }) => void
  disabled?: boolean
}

const TONE_LABEL: Record<AssistantTone, string> = {
  formal: 'Formal',
  casual: 'Casual',
  amigavel: 'Amigável',
  direto: 'Direto',
}

/**
 * Story 3.18 §AC #7 — Step 1: Context.
 *
 * Captures the campaign (optional), the objective free-text, and the tone
 * radio. All other steps are blocked until the objective is filled.
 */
export function AssistantContextStep({
  campaignId,
  objective,
  tone,
  onChange,
  disabled = false,
}: AssistantContextStepProps): React.ReactElement {
  const { data, isLoading } = useQuery<CampaignListResponse>({
    queryKey: ['admin-campaigns', { assistant: true }],
    queryFn: async () => api<CampaignListResponse>('/admin/campaigns'),
  })

  return (
    <div className="grid gap-4">
      <div className="grid gap-2">
        <Label htmlFor="assistant-campaign">Campanha (opcional)</Label>
        <Select
          id="assistant-campaign"
          value={campaignId}
          onChange={(e) => onChange({ campaignId: e.target.value, objective, tone })}
          disabled={isLoading || disabled}
        >
          <option value="">— Sem campanha vinculada —</option>
          {(data?.campaigns ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <p className="text-xs text-muted-foreground">
          Quando uma campanha é selecionada, o assistente usa o prompt ativo como referência.
        </p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="assistant-objective">Objetivo da campanha</Label>
        <Textarea
          id="assistant-objective"
          rows={4}
          required
          value={objective}
          onChange={(e) => onChange({ campaignId, objective: e.target.value, tone })}
          disabled={disabled}
          placeholder="Ex: Confirmar reunião 24h antes via WhatsApp, oferecendo reagendamento se necessário."
        />
        <p className="text-xs text-muted-foreground">
          {objective.length} caracteres · mínimo 10
        </p>
      </div>

      <fieldset
        className="grid gap-2"
        aria-labelledby="assistant-tone-legend"
        disabled={disabled}
      >
        <legend id="assistant-tone-legend" className="text-sm font-medium">
          Tom de voz
        </legend>
        <div className="flex flex-wrap gap-3">
          {ASSISTANT_TONES.map((t) => (
            <label
              key={t}
              className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm has-[input:checked]:border-primary has-[input:checked]:bg-primary/5"
            >
              <input
                type="radio"
                name="assistant-tone"
                value={t}
                checked={tone === t}
                onChange={() => onChange({ campaignId, objective, tone: t })}
                disabled={disabled}
              />
              <span>{TONE_LABEL[t]}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  )
}
